import { useState } from "react";
import { Container, Button } from "react-bootstrap";
import { BsFillCheckCircleFill, BsXCircle } from "react-icons/bs";
import { connection, CONNECTION_THRESHOLDS } from "@/utils/constant";
import CommonLayout from "./commonLayout";
import axios from 'axios';
import { measureUploadSpeed, measurePingAndPacketLoss, checkDatabaseStatus } from "../utils/custom";

const Connection = ({ socket }) => {
  const [btnDisable, setBtnDisable] = useState(false);
  const [btnText, setBtnText] = useState("Start the Test");
  const [status, setStatus] = useState("");
  const [serverConnection, setServerConnection] = useState("");
  const [speed, setSpeed] = useState([]);
  const handleClick = async () => {
    setBtnDisable(true);
    setBtnText("Test in Progress");
    let IsmeasureDownloadSpeed = 0;
    const testFileUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/vfner-admin/neture.jpg?a=${Math.random()}`
    const startDownload = new Date().getTime();
    fetch(testFileUrl)
      .then(response => response.blob())
      .then(async (data) => {
        const endDownload = new Date().getTime();
        const durationInSeconds = (endDownload - startDownload) / 1000;
        const fileSizeInBits = data.size * 18;
        IsmeasureDownloadSpeed = (fileSizeInBits / (1024 * 1024) / durationInSeconds);
        const IsmeasureUploadSpeed = await measureUploadSpeed();
        const IsmeasurePingAndPacketLoss = await measurePingAndPacketLoss();
        const responseServerConnection = await axios.get(`${process.env.REACT_APP_API_URL}/check-server-connection`);
        const responseDatabaseConnection = await checkDatabaseStatus(socket);
        setSpeed([IsmeasureDownloadSpeed.toFixed(2), IsmeasureUploadSpeed, IsmeasurePingAndPacketLoss.ping, IsmeasurePingAndPacketLoss.packetLoss]);
        setStatus(`Database connection: ${responseDatabaseConnection}`);
        setServerConnection(responseServerConnection.data.status);
        setBtnText("Test Again");
        setBtnDisable(false);
      });
  };

  return (
    <CommonLayout userData={true}>
      <Container className="mt-8">
        <div className="mb-8 vstack gap-8">
          <p className="text-md">
            {
              "Use this tool to check your network readiness and ensure a seamless vFNER experience."
            }
          </p>
          <Button
            disabled={btnDisable}
            variant={"secondary"}
            className={`py-3 me-3 mt-2 my-3 px-14 text-common text-futura-medium w-max`}
            onClick={() => handleClick()}
          >
            {btnText}
          </Button>
          <div>
            <h5 className="text-lg font-bolder">{"Connection Speed"}</h5>
            <p className="text-sm mt-2 mb-3">
              {btnText === "Start the Test" && speed?.length === 0
                ? connection?.readyToTest
                : btnText === "Test in Progress" && speed?.length === 0
                  ? connection?.connectionSpeedText
                  : `Download Speed: ${speed[0]} Mbps | Upload Speed: ${speed[1]} Mbps`}
            </p>
            {speed[0] >= CONNECTION_THRESHOLDS.DOWNLOAD_SPEED_GREEN ? (
              <span className="pe-2 lh-none text-success font-bolder text-sm hstack">
                <BsFillCheckCircleFill
                  className="text-success me-2"
                  size={18}
                />
                {connection?.connectionSpeedSuccessText}
              </span>
            ) : speed[0] >= CONNECTION_THRESHOLDS.DOWNLOAD_SPEED_RED && speed[0] < CONNECTION_THRESHOLDS.DOWNLOAD_SPEED_GREEN ? (
              <span className="pe-2 lh-none text-yellow-orange font-bolder text-sm hstack">
                <BsFillCheckCircleFill
                  className="text-yellow-orange me-2"
                  size={18}
                />
                {connection?.connectionSpeedWarningText}
              </span>
            ) : speed[0] < CONNECTION_THRESHOLDS.DOWNLOAD_SPEED_RED ? (
              <span className="pe-2 lh-none text-danger font-bolder text-sm hstack">
                <BsXCircle className="text-danger me-2" size={18} />
                {connection?.connectionSpeedErrorText}
              </span>
            ) : (
              ""
            )}
          </div>

          <div>
            <h5 className="text-lg font-bolder">{"Network Responsiveness"}</h5>
            <p className="text-sm mt-2 mb-3">
              {btnText === "Start the Test" && speed.length === 0
                ? connection?.readyToTest
                : btnText === "Test in Progress" && speed.length === 0
                  ? connection?.networkResponsivenessText
                  : `Ping: ${speed[2]} ms | Packet Loss: ${speed[3]}%`}
            </p>
            {speed[2] < CONNECTION_THRESHOLDS.PING_WARNING ? (
              <span className="pe-2 lh-none text-success font-bolder text-sm hstack">
                <BsFillCheckCircleFill
                  className="text-success me-2"
                  size={18}
                />
                {connection?.networkResponsivenessSuccessText}
              </span>
            ) : speed[2] >= CONNECTION_THRESHOLDS.PING_WARNING && speed[2] < CONNECTION_THRESHOLDS.PING_ERROR ? (
              <span className="pe-2 lh-none text-yellow-orange font-bolder text-sm hstack">
                <BsFillCheckCircleFill
                  className="text-yellow-orange me-2"
                  size={18}
                />
                {connection?.networkResponsivenessWarningText}
              </span>
            ) : speed[2] >= CONNECTION_THRESHOLDS.PING_ERROR ? (
              <span className="pe-2 lh-none text-danger font-bolder text-sm hstack">
                <BsXCircle className="text-danger me-2" size={18} />
                {connection?.networkResponsivenessErrorText}
              </span>
            ) : (
              ""
            )}
          </div>

          <div>
            <h5 className="text-lg font-bolder">{"Server Connection"}</h5>
            <p className="text-sm mt-2 mb-3">
              {btnText === "Start the Test" && serverConnection === ""
                ? connection?.readyToTest
                : btnText === "Test in Progress" && serverConnection === ""
                  ? connection?.serverConnectionText
                  : `Server Connection: ${serverConnection}`}
            </p>
            {serverConnection === "Good" ? (
              <span className="pe-2 lh-none text-success font-bolder text-sm hstack">
                <BsFillCheckCircleFill
                  className="text-success me-2"
                  size={18}
                />
                {connection?.serverConnectionSuccessText}
              </span>
            ) : serverConnection === "Average" ? (
              <span className="pe-2 lh-none text-yellow-orange font-bolder text-sm hstack">
                <BsFillCheckCircleFill
                  className="text-yellow-orange me-2"
                  size={18}
                />
                {connection?.serverConnectionWarningText}
              </span>
            ) : serverConnection === "Bad" ? (
              <span className="pe-2 lh-none text-danger font-bolder text-sm hstack">
                <BsXCircle className="text-danger me-2" size={18} />
                {connection?.serverConnectionErrorText}
              </span>
            ) : (
              ""
            )}
          </div>

          <div>
            <h5 className="text-lg font-bolder">{"Database connection"}</h5>
            <p className="text-sm mt-2 mb-3">
              {btnText === "Start the Test" && status === ""
                ? connection?.readyToTest
                : btnText === "Test in Progress" && status === ""
                  ? connection?.databaseConnectionText
                  : status}
            </p>
            {status === "Database connection: Good" ? (
              <span className="pe-2 lh-none text-success font-bolder text-sm hstack">
                <BsFillCheckCircleFill
                  className="text-success me-2"
                  size={18}
                />
                {connection?.databaseConnectionSuccessText}
              </span>
            ) : status === "Database connection: Average" ? (
              <span className="pe-2 lh-none text-yellow-orange font-bolder text-sm hstack">
                <BsFillCheckCircleFill
                  className="text-yellow-orange me-2"
                  size={18}
                />
                {connection?.databaseConnectionWarningText}
              </span>
            ) : status === "Database connection: Bad" ? (
              <span className="pe-2 lh-none text-danger font-bolder text-sm hstack">
                <BsXCircle className="text-danger me-2" size={18} />
                {connection?.databaseConnectionErrorText}
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
      </Container>
    </CommonLayout>
  );
};
export default Connection;
